import { Controller } from "@hotwired/stimulus"

// Debounced company-domain autosuggest for the hero "map my market" form.
// Typing queries /companies/typeahead (CrunchbaseCompany.typeahead — directory
// matches with logos), renders a dropdown, and on pick fills the domain field
// and submits the form straight into the preview/signup flow. Fully keyboard
// navigable (↑/↓/Enter/Esc) and accessible (combobox/listbox roles).
export default class extends Controller {
  static targets = ["input", "results"]
  static values = {
    url: String,
    delay: { type: Number, default: 140 },
    // true (hero/search): a pick submits the form. false (e.g. the watchlist
    // competitor adder): a pick only fills the field + fires `pick`, and a
    // sibling controller decides what to do with it.
    submit: { type: Boolean, default: true },
  }

  connect() {
    this.items = []
    this.active = -1
    this.token = 0 // guards against out-of-order responses
    this.cache = new Map() // query → results, so backspace/retype doesn't refetch
    this.onDocClick = (e) => { if (!this.element.contains(e.target)) this.close() }
    document.addEventListener("click", this.onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    if (this.timer) clearTimeout(this.timer)
  }

  input() {
    if (this.timer) clearTimeout(this.timer)
    const q = this.inputTarget.value.trim()
    if (q.length < 2) return this.close()
    if (this.cache.has(q)) return this.render(this.cache.get(q), q) // instant repeat
    this.timer = setTimeout(() => this.search(q), this.delayValue)
  }

  async search(q) {
    const mine = ++this.token
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(q)}`
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok || mine !== this.token) return
      const items = await res.json()
      this.cache.set(q, items)
      this.render(items, q)
    } catch (_e) {
      // transient blip — leave any existing dropdown as-is
    }
  }

  render(items, query) {
    // When the typed text is a domain we don't have indexed, always offer a
    // "Continue with X" row so the field never feels broken — many real
    // companies aren't in even a 3M-row directory.
    const rows = items.slice()
    if (offersContinue(query, items)) rows.push({ continue: true, query })

    this.items = rows
    this.active = -1
    if (!rows.length) return this.close()

    this.resultsTarget.innerHTML = rows.map((c, i) =>
      c.continue ? this.continueRow(c, i) : this.companyRow(c, i)).join("")

    this.resultsTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  companyRow(c, i) {
    return `
      <button type="button" role="option" id="ct-opt-${i}" data-index="${i}"
        data-action="mousedown->company-typeahead#pick mouseover->company-typeahead#hover"
        class="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 focus:bg-slate-50">
        ${this.logo(c)}
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium text-cb-navy-900">${esc(c.name)}</span>
          <span class="block truncate text-xs text-slate-400">${esc(c.domain)}</span>
        </span>
      </button>`
  }

  continueRow(c, i) {
    return `
      <button type="button" role="option" id="ct-opt-${i}" data-index="${i}"
        data-action="mousedown->company-typeahead#pick mouseover->company-typeahead#hover"
        class="flex w-full items-center gap-3 px-3 py-2 text-left border-t border-slate-100 hover:bg-slate-50 focus:bg-slate-50">
        <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-blue-50 text-blue-600" aria-hidden="true">→</span>
        <span class="min-w-0 flex-1 text-sm text-slate-600 truncate">Continue with <span class="font-medium text-cb-navy-900">${esc(c.query)}</span></span>
      </button>`
  }

  logo(c) {
    const initialSpan = `Object.assign(document.createElement('span'),{className:'flex h-7 w-7 shrink-0 items-center justify-center rounded bg-slate-100 text-xs font-semibold text-slate-500',textContent:'${esc((c.name[0] || "?").toUpperCase())}'})`
    if (c.logo) {
      // Cascade R2 → Crunchbase → placeholder: on a load error, swap to the alt
      // URL once, then replace with the initial badge.
      const alt = c.logo_alt ? ` data-alt="${esc(c.logo_alt)}"` : ""
      return `<img src="${esc(c.logo)}" alt="" loading="lazy"${alt}
        class="h-7 w-7 shrink-0 rounded bg-white object-contain"
        onerror="if(this.dataset.alt){this.src=this.dataset.alt;this.removeAttribute('data-alt')}else{this.replaceWith(${initialSpan})}">`
    }
    return `<span class="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-slate-100 text-xs font-semibold text-slate-500">${esc((c.name[0] || "?").toUpperCase())}</span>`
  }

  hover(e) {
    this.setActive(Number(e.currentTarget.dataset.index))
  }

  // Keydown on the input (↑/↓/Enter/Esc). Enter with an active option picks it;
  // Enter with none lets the form submit the typed domain normally.
  keydown(e) {
    if (this.resultsTarget.classList.contains("hidden")) return
    if (e.key === "ArrowDown") { e.preventDefault(); this.move(1) }
    else if (e.key === "ArrowUp") { e.preventDefault(); this.move(-1) }
    else if (e.key === "Enter" && this.active >= 0) { e.preventDefault(); this.select(this.items[this.active]) }
    else if (e.key === "Escape") { this.close() }
  }

  move(delta) {
    const n = this.items.length
    if (!n) return
    this.setActive((this.active + delta + n) % n)
  }

  setActive(i) {
    this.active = i
    Array.from(this.resultsTarget.children).forEach((el, idx) => {
      el.classList.toggle("bg-slate-50", idx === i)
      if (idx === i) el.scrollIntoView({ block: "nearest" })
    })
    this.inputTarget.setAttribute("aria-activedescendant", i >= 0 ? `ct-opt-${i}` : "")
  }

  // mousedown (not click) so it beats the input's blur and the doc-click close.
  pick(e) {
    e.preventDefault()
    this.select(this.items[Number(e.currentTarget.dataset.index)])
  }

  select(item) {
    if (!item) return
    // The continue row keeps the typed text as-is; a company row fills its domain.
    if (!item.continue) this.inputTarget.value = item.domain
    this.close()
    this.dispatch("pick", { detail: { value: this.inputTarget.value } })
    if (this.submitValue) this.element.requestSubmit?.()
  }

  close() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
    this.items = []
    this.active = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }
}

function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
}

// Offer "Continue with X" only when the typed text looks like a domain (so it's
// a meaningful value to submit) and isn't already an exact result.
function offersContinue(query, items) {
  const q = String(query || "").trim().toLowerCase()
  if (!/^[^\s]+\.[a-z]{2,}$/.test(q)) return false
  return !items.some((c) => (c.domain || "").toLowerCase() === q)
}
